<template>
  <div
    v-bind="containerProps"
    class="labels-table-container"
  >
    <div v-bind="wrapperProps">
      <table
        class="labels-table"
        role="grid"
        aria-label="Dataset Labels"
      >
        <thead class="labels-table__header">
          <tr>
            <th
              v-for="column in columnHeaders"
              :key="column.id"
              :style="column.style"
              class="labels-table__header-cell"
            >
              {{ column.text }}
            </th>
          </tr>
        </thead>
        <tbody class="labels-table__body">
          <tr
            v-if="showCreateLabelForm"
            class="labels-table__create-label-row"
          >
            <td
              class="labels-table__cell labels-table__cell--create-label"
              colspan="4"
            >
              <InputField
                v-model="newLabelName"
                :max-length="50"
                class="labels-table__create-label-input"
                placeholder="Label name ..."
                size="xs"
                type="text"
                autofocus
                @keyup.enter="handleCreateLabel"
                @keyup.escape="emit('close-create-form')"
              />
            </td>
            <td class="labels-table__cell labels-table__cell--create-label">
              <div class="labels-table__create-label-button-group">
                <IconButton
                  size="xs"
                  flair="circle"
                  kind="transparent"
                  aria-label="Close create label form"
                  @click="emit('close-create-form')"
                >
                  <Icon name="mono-close" />
                </IconButton>
                <IconButton
                  size="xs"
                  flair="circle"
                  kind="transparent"
                  class="labels-table__create-label-button"
                  aria-label="Create label"
                  :disabled="!trimmedLabelName"
                  @click="handleCreateLabel"
                >
                  <Icon name="fill-checked-circled" />
                </IconButton>
              </div>
            </td>
          </tr>
          <tr
            v-for="{ data: datasetLabel } in list"
            :key="datasetLabel.id"
            class="labels-table__row"
            :class="{ 'labels-table__row--hovered': isMenuVisible(datasetLabel.id) }"
            @mouseenter="handleRowHover(datasetLabel.id)"
            @mouseleave="handleRowLeave(datasetLabel.id)"
            @focus="handleRowHover(datasetLabel.id)"
            @blur="handleRowLeave(datasetLabel.id)"
          >
            <td class="labels-table__cell">
              <Badge
                :color="datasetLabel.color"
                :label="datasetLabel.name"
                :override-label-color="getMatchingTextColor(datasetLabel.color)"
                no-capitalize
                size="large"
                class="labels-table__badge"
              />
            </td>
            <td class="labels-table__cell">{{ datasetLabel.dataset_ids.length }} Datasets</td>
            <td class="labels-table__cell">
              {{ formatDateToMonthYear(datasetLabel.inserted_at) }}
            </td>
            <td class="labels-table__cell">
              {{
                datasetLabel.updated_at === datasetLabel.inserted_at
                  ? '-'
                  : formatDateToMonthYear(datasetLabel.updated_at)
              }}
            </td>
            <td
              class="labels-table__cell"
              align="right"
            >
              <DatasetLabelEditMenu
                :dataset-label="datasetLabel"
                :class="{ 'labels-table__edit-menu--visible': isMenuVisible(datasetLabel.id) }"
                class="labels-table__edit-menu"
                can-delete
                @update:dataset-label="handleLabelUpdate($event, datasetLabel)"
                @delete:dataset-label="handleDeleteLabel(datasetLabel)"
                @state:change="handleMenuStateChange($event, datasetLabel.id)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <ConfirmDialog
    v-model:open="showDeleteDialog"
    type="negative"
    @confirm="onConfirmDeleteLabel"
  >
    <template #title> Are you sure you want delete this label? </template>
    <template #detail>
      <div
        v-if="labelToDelete"
        class="labels-table__delete-label-dialog-detail"
      >
        <p>
          <Badge
            :label="`${labelToDelete.dataset_ids.length} Datasets`"
            :color="grayColorPreset?.bgColor"
            :override-label-color="grayColorPreset?.textColor"
            size="large"
            no-tooltip
          />
          are connected with this label.
        </p>
        <p v-if="labelToDelete.dataset_ids.length > 0">All these connections will be lost.</p>
        <p>
          <strong class="labels-table__delete-label-dialog-detail-warning">
            This action can't be undone.
          </strong>
        </p>
      </div>
    </template>
    <template #cancel-text> No, go back </template>
    <template #confirm-text> Yes, delete this label </template>
  </ConfirmDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { useVirtualList } from '@vueuse/core'

import Badge from '@/uiKit/Badge/Badge.vue'
import IconButton from '@/uiKit/Button/IconButton.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import InputField from '@/components/Common/InputField/V2/InputField.vue'
import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import { formatDateToMonthYear } from '@/core/utils/time'
import type { DatasetLabelPayload } from '@/store/types/DatasetLabelPayload'
// eslint-disable-next-line boundaries/element-types
import { getMatchingTextColor, getPresetColorByName } from '@/modules/Datasets/datasetLabelUtils'
// eslint-disable-next-line boundaries/element-types
import DatasetLabelEditMenu from '@/modules/Datasets/DatasetLabelEditMenu.vue'

const props = defineProps<{
  labels: DatasetLabelPayload[]
  showCreateLabelForm: boolean
}>()

const emit = defineEmits<{
  (e: 'close-create-form'): void
  (e: 'create-label', name: string): void
  (e: 'update-label', label: DatasetLabelPayload, data: { name: string; color: string }): void
  (e: 'delete-label', label: DatasetLabelPayload): void
}>()

const newLabelName = ref('')
const hoveredRowId = ref<string | null>(null)
const openMenuId = ref<string | null>(null)
const showDeleteDialog = ref(false)
const labelToDelete = ref<DatasetLabelPayload | null>(null)
const columnHeaders = ref([
  { id: 'name', text: 'Name', style: { width: '220px' } },
  { id: 'usage', text: 'Usage', style: { width: '120px' } },
  { id: 'created', text: 'Created', style: { width: '80px' } },
  { id: 'updated', text: 'Updated', style: { width: '80px' } },
  { id: 'actions', text: '', style: { width: '52px' } },
])

const trimmedLabelName = computed(() => newLabelName.value.trim())
const datasetLabels = computed(() => props.labels)
const grayColorPreset = computed(() => getPresetColorByName('Gray'))

const { list, containerProps, wrapperProps } = useVirtualList(datasetLabels, {
  itemHeight: 40,
  overscan: 5,
})

const handleCreateLabel = (): void => {
  if (!trimmedLabelName.value) return

  emit('create-label', trimmedLabelName.value)
}

const handleLabelUpdate = (
  data: { name: string; color: string },
  label: DatasetLabelPayload,
): void => {
  emit('update-label', label, data)
}

const handleDeleteLabel = (label: DatasetLabelPayload): void => {
  labelToDelete.value = label
  showDeleteDialog.value = true
}

const onConfirmDeleteLabel = (): void => {
  if (!labelToDelete.value) return

  emit('delete-label', labelToDelete.value)
  nextTick(() => {
    showDeleteDialog.value = false
    labelToDelete.value = null
  })
}

const handleRowHover = (id: string): void => {
  hoveredRowId.value = id
}

const handleRowLeave = (id: string): void => {
  if (hoveredRowId.value === id && openMenuId.value !== id) {
    hoveredRowId.value = null
  }
}

const handleMenuStateChange = (isOpen: boolean, id: string): void => {
  openMenuId.value = isOpen ? id : null
}

const isMenuVisible = (id: string): boolean => hoveredRowId.value === id || openMenuId.value === id

watch(
  () => props.showCreateLabelForm,
  () => {
    newLabelName.value = ''
  },
)
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/spacings.scss';

$table-border-radius: 10px;
$table-cell-padding: $spacing-4;
$table-transition-duration: 0.1s;

.labels-table-container {
  height: 100%;
  padding: 16px 32px;
}

.labels-table {
  text-align: left;
  border-spacing: 0 $spacing-2;
  width: 100%;
  max-width: 768px;
  margin: auto;

  &__header {
    @include typographyRegularBody100;
    color: var(--color-text-subtlest);

    &-cell:first-child {
      padding-inline: $spacing-2;
    }
  }

  &__body {
    @include typographyRegularBody100;
    color: var(--color-text-subtle);
  }

  &__cell {
    vertical-align: middle;
    padding-block: $spacing-4;

    &:first-child {
      border-radius: $table-border-radius 0 0 $table-border-radius;

      &:not(.labels-table__cell--create-label) {
        padding: $spacing-2;
      }
    }

    &:last-child {
      border-radius: 0 10px 10px 0;
      padding-block: 0;
    }

    &--create-label {
      padding: $spacing-4;

      &:last-child {
        padding-inline-start: 0;
        padding-inline-end: $spacing-2;
      }
    }
  }

  &__row {
    will-change: background-color;
    transition: background-color $table-transition-duration ease-out;

    &--hovered {
      background-color: var(--color-background-transparent-hovered);
    }
  }

  &__create-label-row {
    background-color: var(--color-background-selected);
  }

  &__create-label-input {
    :deep(.inputfield__container) {
      height: 16px;
      padding: 0;
      border: none;
      outline: none;
      background-color: transparent;

      &:hover {
        background-color: transparent;
      }
    }

    :deep(.inputfield) {
      @include typographyRegularBody100;
    }
  }

  &__badge {
    justify-content: flex-start;
    max-width: 300px;
  }

  &__create-label-button-group {
    display: flex;
    justify-content: flex-end;
  }

  &__create-label-button:not(:disabled) {
    :deep(.darwin-icon) {
      color: var(--color-text-selected);
    }
  }

  &__edit-menu {
    opacity: 0;
    pointer-events: none;

    &--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__delete-label-dialog-detail {
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    color: var(--color-text-subtle);

    &-warning {
      color: var(--color-text-warning);
    }

    .badge {
      display: inline-flex;
    }
  }
}
</style>
